import React, { useMemo } from 'react';
import { AlertTriangle, Info, X, Upload, Settings } from 'lucide-react';

/**
 * MissingDataAlert - Shows alerts when critical data is missing for forecasting
 *
 * @param {Object} account - The current forecast account
 * @param {Array} rowData - Array of monthly forecast data rows
 * @param {string} selectedDriver - Currently selected driver type
 * @param {function} onDismiss - Callback when alert is dismissed
 */
const MissingDataAlert = ({ account, rowData = [], selectedDriver, onDismiss }) => {
  // Analyze data to detect missing information
  const alerts = useMemo(() => {
    const issues = [];

    if (!account || !rowData || rowData.length === 0) return issues;

    // Check for missing driver
    if (!selectedDriver || selectedDriver === '') {
      issues.push({
        id: 'no-driver',
        severity: 'warning',
        title: 'No Forecast Driver Selected',
        message: 'This account does not have a forecast driver configured. Select a driver to begin forecasting.',
        action: {
          label: 'Select Driver',
          icon: Settings
        }
      });
    }

    // Check for room-based drivers without room data
    const roomBasedDrivers = ['$ / ARN', '$ / ARN LM', '$ / ORN', '$ / ORN LM', 'REVPAR Projection'];
    if (roomBasedDrivers.includes(selectedDriver)) {
      const hasRoomData = rowData.some(row => row.availableRooms > 0 || row.occupiedRooms > 0);
      if (!hasRoomData) {
        issues.push({
          id: 'no-room-data',
          severity: 'error',
          title: 'Missing Room Statistics',
          message: `The "${selectedDriver}" driver requires hotel room data, but none has been imported for any month this year.`,
          action: {
            label: 'Import Room Data',
            icon: Upload
          }
        });
      }
    }

    // Check for partial room data (some months missing)
    if (roomBasedDrivers.includes(selectedDriver)) {
      const monthsWithoutRooms = rowData.filter(row =>
        row.availableRooms === 0 && row.occupiedRooms === 0
      ).length;

      if (monthsWithoutRooms > 0 && monthsWithoutRooms < 12) {
        issues.push({
          id: 'partial-room-data',
          severity: 'warning',
          title: 'Incomplete Room Data',
          message: `${monthsWithoutRooms} month(s) are missing room statistics. These months will show $0 forecasts.`,
          action: {
            label: 'Import Missing Data',
            icon: Upload
          }
        });
      }
    }

    // Check for Budget driver without budget data
    if (selectedDriver === 'Budget') {
      const hasBudgetData = rowData.some(row => row.budget > 0);
      if (!hasBudgetData) {
        issues.push({
          id: 'no-budget',
          severity: 'error',
          title: 'Missing Budget Data',
          message: 'The Budget driver is selected, but no budget amounts have been imported for this account.',
          action: {
            label: 'Import Budget',
            icon: Upload
          }
        });
      }
    }

    // Check for OTB/RMS forecast drivers without imported forecasts
    if (selectedDriver === 'OTB Forecast' || selectedDriver === 'RMS Forecast') {
      const hasForecastData = rowData.some(row =>
        selectedDriver === 'OTB Forecast'
          ? row.otbForecastRevenue > 0
          : row.rmsForecastRevenue > 0
      );

      if (!hasForecastData) {
        const systemName = selectedDriver === 'OTB Forecast' ? 'On-The-Books' : 'RMS';
        issues.push({
          id: 'no-imported-forecast',
          severity: 'error',
          title: `Missing ${systemName} Data`,
          message: `The "${selectedDriver}" driver is selected, but no ${systemName.toLowerCase()} forecast data has been imported from your hotel system.`,
          action: {
            label: 'Import Forecast Data',
            icon: Upload
          }
        });
      }
    }

    // Check for input-based drivers without input values
    const inputBasedDrivers = ['$ / ARN', '$ / ORN', 'REVPAR Projection', 'Static Entry'];
    if (inputBasedDrivers.includes(selectedDriver)) {
      const monthsWithoutInput = rowData.filter(row => !row.input || row.input === 0).length;

      if (monthsWithoutInput === 12) {
        issues.push({
          id: 'no-input',
          severity: 'info',
          title: 'Input Values Needed',
          message: `Enter input values in the table to generate forecasts with the "${selectedDriver}" driver.`,
          action: {
            label: 'Enter Values',
            icon: Settings
          }
        });
      } else if (monthsWithoutInput > 0 && monthsWithoutInput < 12) {
        issues.push({
          id: 'partial-input',
          severity: 'info',
          title: 'Some Months Missing Input',
          message: `${monthsWithoutInput} month(s) don't have input values and will show $0 forecasts.`,
          action: null
        });
      }
    }

    return issues;
  }, [account, rowData, selectedDriver]);

  // Don't render if no alerts
  if (alerts.length === 0) return null;

  // Severity colors
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          icon: 'text-red-600',
          button: 'text-red-600 hover:text-red-700 hover:bg-red-100'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-800',
          icon: 'text-yellow-600',
          button: 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100'
        };
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          icon: 'text-blue-600',
          button: 'text-blue-600 hover:text-blue-700 hover:bg-blue-100'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          icon: 'text-gray-600',
          button: 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
        };
    }
  };

  return (
    <div className="space-y-2 mb-3">
      {alerts.map((alert) => {
        const styles = getSeverityStyles(alert.severity);
        const Icon = alert.severity === 'info' ? Info : AlertTriangle;
        const ActionIcon = alert.action?.icon;

        return (
          <div
            key={alert.id}
            className={`p-3 rounded-lg border ${styles.bg} ${styles.border} flex items-start gap-3`}
          >
            {/* Alert Icon */}
            <Icon className={`flex-shrink-0 mt-0.5 ${styles.icon}`} size={20} />

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <h4 className={`text-sm font-semibold ${styles.text} mb-0.5`}>
                    {alert.title}
                  </h4>
                  <p className={`text-xs ${styles.text}`}>
                    {alert.message}
                  </p>
                </div>

                {/* Dismiss button */}
                {onDismiss && (
                  <button
                    onClick={() => onDismiss(alert.id)}
                    className={`flex-shrink-0 transition-colors ${styles.button} p-1 rounded`}
                    title="Dismiss"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Action button */}
              {alert.action && (
                <button
                  className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${styles.button} border ${styles.border}`}
                >
                  {ActionIcon && <ActionIcon size={14} />}
                  {alert.action.label}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MissingDataAlert;
